import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../utils/formatters';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { campaignLabel, platformLabel } from '../campaignLabel';

const MEDIA_WIDTH = 300; // px on md+; height stretches to card, min 2:3

function FieldLabel({ children }) {
  return (
    <Typography variant="overline" color="text.secondary">
      {children}
    </Typography>
  );
}

function Field({ label, children }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.5}>
      <FieldLabel>{label}</FieldLabel>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {children || t('notAvailable')}
      </Typography>
    </Stack>
  );
}

function CampaignMedia({ src, alt, placeholder }) {
  // `src` may be empty, or a URL whose image has expired (model notes it "may
  // expire"). `onError` covers the second case — both fall back to the placeholder.
  const [failed, setFailed] = React.useState(false);

  const sx = {
    width: { xs: '100%', md: MEDIA_WIDTH },
    minHeight: { xs: 240, md: MEDIA_WIDTH * 1.5 },
    alignSelf: 'stretch',
    flexShrink: 0,
  };

  if (src && !failed) {
    return (
      <CardMedia
        component="img"
        image={src}
        alt={alt}
        onError={() => setFailed(true)}
        sx={{ ...sx, objectFit: 'cover' }}
      />
    );
  }

  return (
    <Box
      sx={{
        ...sx,
        bgcolor: 'background.default',
        backgroundImage: (theme) =>
          `repeating-linear-gradient(45deg, ${(theme.vars || theme).palette.action.hover} 0, ${(theme.vars || theme).palette.action.hover} 8px, transparent 8px, transparent 16px)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        p: 2,
        color: 'text.disabled',
      }}
    >
      <ImageNotSupportedOutlinedIcon sx={{ fontSize: 48 }} />
      <Typography variant="caption" fontFamily="monospace" textAlign="center">
        {placeholder}
      </Typography>
    </Box>
  );
}

/**
 * Snapshot of the ad a campaign came from (Meta CTWA referral): 2:3 ad image
 * (on top below `md`), title + platform chip + "open ad", the ad body and the
 * remaining fields. Shared by the dashboard and mobile detail pages, so it
 * knows nothing about routes: page-specific buttons come in through `actions`
 * and render in the CardActions at the end of the card.
 */
export default function CampaignAdCard({ campaign, actions }) {
  const { t, i18n } = useTranslation();
  const f = (key) => t(`campaigns.fields.${key}`);

  return (
    <Card sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      <CampaignMedia
        src={campaign.image_url}
        alt={f('image')}
        placeholder={`${f('image')} · 2:3`}
      />

      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <CardHeader
          sx={{ alignItems: 'flex-start' }}
          title={<Typography variant="h5">{campaignLabel(campaign)}</Typography>}
          subheader={
            <Chip
              size="small"
              label={platformLabel(campaign.platform)}
              color="primary"
              sx={{ mt: 0.5 }}
            />
          }
          action={
            campaign.source_url && (
              <Button
                variant="contained"
                size="small"
                endIcon={<NorthEastIcon />}
                href={campaign.source_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('campaigns.actions.openAd')}
              </Button>
            )
          }
        />

        <Divider />

        <CardContent>
          <Stack spacing={0.5}>
            <FieldLabel>{f('body')}</FieldLabel>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {campaign.body || t('notAvailable')}
            </Typography>
          </Stack>
        </CardContent>

        <Divider />

        <CardContent sx={{ bgcolor: 'background.default', flex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }}>
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <Field label={f('externalId')}>{campaign.external_id}</Field>
              <Field label={f('mediaType')}>{campaign.media_type}</Field>
            </Stack>
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <Field label={f('sourceUrl')}>
                {campaign.source_url && (
                  <Link href={campaign.source_url} target="_blank" rel="noopener noreferrer">
                    {campaign.source_url}
                  </Link>
                )}
              </Field>
              <Field label={f('createdAt')}>
                {formatDateTime(campaign.created_at, i18n.language, { empty: t('notAvailable') })}
              </Field>
            </Stack>
          </Stack>
        </CardContent>

        {actions && (
          <>
            <Divider />
            <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
              {actions}
            </CardActions>
          </>
        )}
      </Stack>
    </Card>
  );
}
